import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { fetchProfile } from "./ProfileService";
import { fetchCurrency } from "./CurrencyService";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export type ServiceProviderType = "MNP" | "HLR" | "MNP_HLR";
export type ServiceProviderProtocol = "HTTP_REST";
export type FieldLocation = "URL" | "BODY" | "HEADER";

export interface IRequestFieldItem {
  name: string;
  location: FieldLocation;
}

export interface IRequestField {
  apiKey: IRequestFieldItem;
  msisdn: IRequestFieldItem;
  username: IRequestFieldItem;
  password: IRequestFieldItem;
  httpMethod: string;
  contentType: string;
}

export interface IResponseField {
  mcc: string;
  mnc: string;
  originalMnc: string;
  ported: string;
  notActive: string;
  notActiveValues: string;
  responseCode: string;
}

export interface IServiceProvider {
  id: number;
  name: string;
  type: ServiceProviderType;
  protocol: ServiceProviderProtocol;
  baseUrl: string;
  apiKey: string;
  username: string;
  password: string;
  requestField: IRequestField;
  responseField: IResponseField;
  enabled: boolean;
  isDeleted?: boolean;
  createdBy?: string;
  updatedBy?: string;
  timestamp?: string;
  createdDate?: string;
  version?: number;
  profileId: string;
}

export interface IServiceProviderHistory {
  id: string;
  snapshot: IServiceProvider & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  status: string;
  createdBy: string;
}

// Fetch all service providers with pagination
export async function fetchServiceProviders(
  params: Params<IServiceProvider>
): Promise<IPageable<IServiceProvider>> {
  try {
    const response = await axiosInstance.get("/service-providers", { params });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching service providers:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching service providers."
    );
  }
}

// Fetch all service providers (no pagination)
export async function fetchAllServiceProviders(
  filter?: string
): Promise<IServiceProvider[]> {
  try {
    const response = await axiosInstance.get("/service-providers/all", {
      params: filter ? { filter } : {},
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching all service providers:", error);
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching all service providers."
    );
  }
}

// Get service provider by ID
export async function getServiceProviderById(
  id: number | string
): Promise<IServiceProvider> {
  try {
    const response = await axiosInstance.get(`/service-providers/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the service provider."
    );
  }
}

// Create new service provider
export async function addServiceProviderApi(
  data: Partial<IServiceProvider>
): Promise<IServiceProvider> {
  try {
    const response = await axiosInstance.post("/service-providers", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the service provider."
    );
  }
}

// Update service provider
export async function editServiceProviderApi(
  id: number | string,
  data: Partial<IServiceProvider>
): Promise<IServiceProvider> {
  try {
    const response = await axiosInstance.put(`/service-providers/${id}`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the service provider."
    );
  }
}

// Delete service provider
export async function deleteServiceProviderApi(
  id: number | string
): Promise<void> {
  try {
    await axiosInstance.delete(`/service-providers/${id}`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the service provider."
    );
  }
}

export async function fetchServiceProviderHistory(
  id: IServiceProvider["id"],
  params: Params<IServiceProvider> = {}
): Promise<IPageable<IServiceProvider>> {
  try {
    const response = await axiosInstance.get(`/service-providers/history`, {
      params: { mainId: id, sort: "historyTimestamp,desc", ...params },
    });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the Service Provider history for ID ${id}.`
    );
  }
}

// React Query hook for service providers
export const useServiceProvidersQuery = (
  params: Params<IServiceProvider>
): UseQueryResult<IPageable<IServiceProvider>, Error> => {
  const effectiveParams = {
    ...params,
    isDeleted: false,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["serviceProviders", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchServiceProviders(effectiveParams),
  });
};

// Get service provider options for dropdowns
export async function getServiceProviderOptions(): Promise<
  { label: string; value: number }[]
> {
  try {
    const providers = await fetchAllServiceProviders();
    return providers.map((provider) => ({
      label: provider.name,
      value: provider.id,
    }));
  } catch (error: any) {
    console.error("Error fetching service provider options:", error);
    return [];
  }
}

// Get service provider name by ID
export async function getServiceProviderName(
  id: number | string
): Promise<string> {
  try {
    const provider = await getServiceProviderById(id);
    return provider.name;
  } catch (error: any) {
    console.error("Error fetching service provider name:", error);
    return "Unknown";
  }
}

// React Query options for table field display (returns provider name)
export const getServiceProviderNameOptions = (serviceProviderId: number | string) => ({
  queryKey: ["serviceProviderName", serviceProviderId],
  queryFn: async (): Promise<string> => {
    try {
      const serviceProvider = await getServiceProviderById(serviceProviderId);
      return serviceProvider?.name || "-";
    } catch {
      return "-";
    }
  },
  select: (name: string) => name,
});

// React Query options for service provider currency (if profileId exists)
export const getServiceProviderCurrencyOptions = (serviceProviderId: number | string) => ({
  queryKey: ["serviceProviderCurrency", serviceProviderId],
  queryFn: async (): Promise<string> => {
    try {
      const serviceProvider = await getServiceProviderById(serviceProviderId);
      const profile = await fetchProfile(serviceProvider.profileId);
      const currency = await fetchCurrency(Number(profile.currencyId));
      return currency?.name || "-";
    } catch {
      return "-";
    }
  },
  select: (currency: string) => currency,
});
